import { createSlice } from '@reduxjs/toolkit';

// Server-synced recent searches — entries are either plain text queries
// (entity_type="query") or typed entities (artist/track/mix). Each row carries
// a `surface` ("mixpedia" | "planner") so Mixpedia and Set Planner each have
// their own list. Normalized to camelCase at the boundary so the UI can render
// without thinking about the API shape.
const searchHistorySlice = createSlice({
    name: 'searchHistory',
    initialState: {
        items: [],
        // loadedSurfaces is a set-like map: surface -> true once we've fetched
        // its rows at least once. Lets each consumer decide whether to fire
        // an initial fetch without re-fetching shared state.
        loadedSurfaces: {},
        loading: false,
    },
    reducers: {
        // Replaces only the rows for the given surface, leaving other surfaces
        // intact. Without this a Planner fetch would wipe Mixpedia's rows.
        setItems(state, action) {
            const { surface, rows } = action.payload;
            state.items = [
                ...rows,
                ...state.items.filter(e => e.surface !== surface),
            ];
            state.loadedSurfaces[surface] = true;
        },
        upsertItem(state, action) {
            const entry = action.payload;
            const surface = entry.surface || 'mixpedia';
            const keyOf = (e) => e.entityType === 'query'
                ? `q:${(e.queryText || '').trim().toLowerCase()}`
                : `${e.entityType}:${e.entityPublicId || ''}`;
            const key = keyOf(entry);
            // When upserting a canonical entry (real publicId), also drop
            // any earlier placeholder entry for the same entityType+name and
            // any sibling row that matches by name+subtitle (different
            // publicId for the same conceptual track). Without this, optimistic
            // typed-Enter entries leak alongside the canonical entry, and the
            // same track surfaced via different autocomplete sources stacks
            // up as multiple rows.
            const incomingPid = String(entry.entityPublicId || '');
            const isCanonical = entry.entityType !== 'query'
                && incomingPid
                && !incomingPid.startsWith('name:')
                && !incomingPid.startsWith('q:');
            const incomingName = (entry.entityName || '').toLowerCase();
            const incomingSub = (entry.entitySubtitle || '').toLowerCase();
            state.items = [entry, ...state.items.filter(e => {
                if ((e.surface || 'mixpedia') !== surface) return true;
                const k = keyOf(e);
                if (k === key) return false;
                if (isCanonical && incomingName
                    && e.entityType === entry.entityType
                    && (e.entityName || '').toLowerCase() === incomingName
                    && (e.entitySubtitle || '').toLowerCase() === incomingSub) {
                    return false;
                }
                return true;
            })];
        },
        removeItem(state, action) {
            state.items = state.items.filter(e => e.id !== action.payload);
        },
        clearAll(state) {
            state.items = [];
            state.loadedSurfaces = {};
        },
        clearSurface(state, action) {
            const surface = action.payload;
            state.items = state.items.filter(e => (e.surface || 'mixpedia') !== surface);
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
    },
});

export const {
    setItems,
    upsertItem,
    removeItem,
    clearAll,
    clearSurface,
    setLoading,
} = searchHistorySlice.actions;

export const clearSearchHistoryState = clearAll;

// Memoized per-(type, surface) filter so useSelector consumers don't re-render
// on every items mutation when their slice hasn't changed.
const selectHistoryItems = (state) => state.searchHistory.items;
const byTypeSurfaceCache = new WeakMap();
export const selectHistoryByType = (type, surface = 'mixpedia') => (state) => {
    const items = selectHistoryItems(state);
    let cache = byTypeSurfaceCache.get(items);
    if (!cache) {
        cache = {};
        byTypeSurfaceCache.set(items, cache);
    }
    const key = `${type}::${surface}`;
    if (!cache[key]) {
        cache[key] = items.filter(e =>
            e.entityType === type && (e.surface || 'mixpedia') === surface
        );
    }
    return cache[key];
};

export const selectSurfaceLoaded = (surface) => (state) =>
    !!state.searchHistory.loadedSurfaces[surface];

export default searchHistorySlice.reducer;
